import React, { useState, useEffect } from 'react';
import { fetchCountries, fetchRegionsByCountry, fetchCitiesByRegion, FALLBACK_COUNTRIES } from 'services/geographyService';

/**
 * Composant de sélection géographique en cascade avec fallback manuel
 * @param {Object} props
 * @param {Function} props.register - react-hook-form register function
 * @param {Object} props.errors - react-hook-form errors object
 * @param {Function} props.setValue - react-hook-form setValue function
 */
const GeographySelector = ({ errors, setValue }) => {
    // États
    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const [cities, setCities] = useState([]);

    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedCity, setSelectedCity] = useState('');

    const [manualCountry, setManualCountry] = useState(false);
    const [manualRegion, setManualRegion] = useState(false);
    const [manualCity, setManualCity] = useState(false);

    const [apiFailure, setApiFailure] = useState(false);
    const [loading, setLoading] = useState({ countries: false, regions: false, cities: false });

    // Mode manuel (si API échoue)
    const [manualMode, setManualMode] = useState(false);

    // Charger les pays au montage du composant
    useEffect(() => {
        loadCountries();
    }, []);

    const loadCountries = async () => {
        setLoading(prev => ({ ...prev, countries: true }));
        try {
            const data = await fetchCountries();
            setCountries(data);
            setApiFailure(false);
        } catch (error) {
            console.error('Échec du chargement des pays, utilisation des données de fallback', error);
            setCountries(FALLBACK_COUNTRIES);
            setApiFailure(true);
        } finally {
            setLoading(prev => ({ ...prev, countries: false }));
        }
    };

    const handleCountryChange = async (e) => {
        const countryCode = e.target.value;
        setSelectedCountry(countryCode);
        setSelectedRegion('');
        setSelectedCity('');
        setRegions([]);
        setCities([]);
        setManualMode(false);

        // Gestion "Autre"
        if (countryCode === 'OTHER') {
            setManualCountry(true);
            setManualRegion(true); // Si pays manuel, région et ville aussi
            setManualCity(true);
            setValue('pays', '');
            setValue('region', '');
            setValue('ville', '');
            return;
        } else {
            setManualCountry(false);
            setManualRegion(false);
            setManualCity(false);
        }

        const country = countries.find(c => c.code === countryCode);
        setValue('pays', country?.name || '');
        setValue('region', '');
        setValue('ville', '');

        if (!countryCode || !country) return;

        // Charger les régions
        setLoading(prev => ({ ...prev, regions: true }));
        try {
            const regionsData = await fetchRegionsByCountry(country.geonameId);
            setRegions(regionsData);

            // Si pas de régions, activer le mode manuel pour région/ville
            if (regionsData.length === 0) {
                setManualRegion(true);
                setManualCity(true);
            }
        } catch (error) {
            console.error('Impossible de charger les régions', error);
            setManualRegion(true);
            setManualCity(true);
        } finally {
            setLoading(prev => ({ ...prev, regions: false }));
        }
    };

    const handleRegionChange = async (e) => {
        const regionValue = e.target.value;

        if (manualMode) {
            setSelectedRegion(regionValue);
            setValue('region', regionValue);
            return;
        }

        // Gestion "Autre"
        if (regionValue === 'OTHER') {
            setManualRegion(true);
            setManualCity(true); // Si région manuelle, ville aussi
            setSelectedRegion('OTHER');
            setValue('region', '');
            setValue('ville', '');
            return;
        } else {
            setManualRegion(false);
            setManualCity(false);
        }

        const region = regions.find(r => r.geonameId === parseInt(regionValue));
        setSelectedRegion(regionValue);
        setSelectedCity('');
        setCities([]);

        setValue('region', region?.name || '');
        setValue('ville', '');

        if (!regionValue) return;

        // Charger les villes
        setLoading(prev => ({ ...prev, cities: true }));
        try {
            const citiesData = await fetchCitiesByRegion(regionValue);
            setCities(citiesData);

            // Si pas de villes, le champ ville deviendra automatiquement un input
            // grâce à la condition cities.length === 0 dans le JSX
        } catch (error) {
            console.error('Impossible de charger les villes', error);
        } finally {
            setLoading(prev => ({ ...prev, cities: false }));
        }
    };

    const handleCityChange = (e) => {
        const cityValue = e.target.value;
        setSelectedCity(cityValue);

        if (manualMode) {
            setValue('ville', cityValue);
            return;
        }

        // Gestion "Autre"
        if (cityValue === 'OTHER') {
            setManualCity(true);
            setValue('ville', '');
        } else {
            setManualCity(false);
            const city = cities.find(c => c.name === cityValue);
            setValue('ville', city?.name || cityValue);
        }
    };

    return (
        <>
            {/* Indicateur de statut API */}
            {apiFailure && (
                <div style={{ padding: '10px', marginBottom: '10px', backgroundColor: '#fff3cd', borderRadius: '5px', fontSize: '12px' }}>
                    ⚠️ Service de géolocalisation limité. Données de base disponibles.
                </div>
            )}

            {/* Pays */}
            <div className="form-group" style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "0px 10px" }}>
                <label htmlFor="pays" className="form-label" style={{ width: "35%" }}>
                    Pays* :
                </label>
                {manualCountry ? (
                    <div style={{ width: "100%", display: 'flex', gap: '5px' }}>
                        <input
                            className="form-control custom-rounded"
                            placeholder="Saisir le pays"
                            onChange={(e) => setValue('pays', e.target.value)}
                            style={{ flex: 1 }}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                                setManualCountry(false);
                                setSelectedCountry('');
                                setValue('pays', '');
                            }}
                        >
                            Liste
                        </button>
                    </div>
                ) : (
                    <select
                        className="form-control custom-rounded"
                        id="pays"
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        style={{ width: "100%" }}
                        disabled={loading.countries}
                    >
                        <option value="">- Sélectionnez un pays -</option>
                        {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                                {country.name}
                            </option>
                        ))}
                        <option value="OTHER">Autre (Saisir manuellement)</option>
                    </select>
                )}
                {errors.pays && (
                    <div className="text-danger">{errors.pays.message}</div>
                )}
            </div>

            {/* Région */}
            <div className="form-group" style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "0px 10px" }}>
                <label htmlFor="region" className="form-label" style={{ width: "35%" }}>
                    Région {!manualMode && <span>*</span>} :
                </label>
                {manualMode || manualRegion || regions.length === 0 ? (
                    <div style={{ width: "100%", display: 'flex', gap: '5px' }}>
                        <input
                            className="form-control custom-rounded"
                            id="region"
                            placeholder="Entrez la région"
                            onChange={(e) => setValue('region', e.target.value)}
                            style={{ flex: 1 }}
                        />
                        {!manualMode && regions.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                    setManualRegion(false);
                                    setSelectedRegion('');
                                    setValue('region', '');
                                }}
                            >
                                Liste
                            </button>
                        )}
                    </div>
                ) : (
                    <select
                        className="form-control custom-rounded"
                        id="region"
                        value={selectedRegion}
                        onChange={handleRegionChange}
                        style={{ width: "100%" }}
                        disabled={loading.regions || !selectedCountry}
                    >
                        <option value="">- Sélectionnez une région -</option>
                        {regions.map((region) => (
                            <option key={region.geonameId} value={region.geonameId}>
                                {region.name}
                            </option>
                        ))}
                        <option value="OTHER">Autre (Saisir manuellement)</option>
                    </select>
                )}
                {errors.region && (
                    <div className="text-danger">{errors.region.message}</div>
                )}
            </div>

            {/* Ville */}
            <div className="form-group" style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "0px 10px" }}>
                <label htmlFor="ville" className="form-label" style={{ width: "35%" }}>
                    Ville :
                </label>
                {manualMode || manualCity || cities.length === 0 ? (
                    <div style={{ width: "100%", display: 'flex', gap: '5px' }}>
                        <input
                            className="form-control custom-rounded"
                            id="ville"
                            placeholder="Entrez la ville"
                            onChange={(e) => setValue('ville', e.target.value)}
                            style={{ flex: 1 }}
                        />
                        {!manualMode && cities.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                    setManualCity(false);
                                    setSelectedCity('');
                                    setValue('ville', '');
                                }}
                            >
                                Liste
                            </button>
                        )}
                    </div>
                ) : (
                    <select
                        className="form-control custom-rounded"
                        id="ville"
                        value={selectedCity}
                        onChange={handleCityChange}
                        style={{ width: "100%" }}
                        disabled={loading.cities || !selectedRegion}
                    >
                        <option value="">- Sélectionnez une ville -</option>
                        {cities.map((city) => (
                            <option key={city.name} value={city.name}>
                                {city.name}
                            </option>
                        ))}
                        <option value="OTHER">Autre (Saisir manuellement)</option>
                    </select>
                )}
                {errors.ville && (
                    <div className="text-danger">{errors.ville.message}</div>
                )}
            </div>
        </>
    );
};

export default GeographySelector;
